import { createContext, useContext, useMemo } from 'react';
import useStore from '../../store/useStore';

/**
 * Several dashboards can share one page in a continuous scroll. Widgets inside
 * one of them read that dashboard's settings from here instead of the store,
 * which only holds the settings of the single current dashboard.
 */
export const WallSectionContext = createContext(null);

export function useDashboardDateRange() {
  const section = useContext(WallSectionContext);
  const current = useStore((state) => state.dashboardDateRange);
  return section ? section.dateRange : current;
}

const NO_FILTERS = [];

/**
 * The dashboard filters a widget should apply, as [{ member, values }]. A
 * filter set by clicking this widget is left out, so it keeps showing every
 * value. Dashboards joined on a wall are not filtered.
 */
export function useDashboardFilters(widgetId) {
  const section = useContext(WallSectionContext);
  const chosen = useStore((state) => state.dashboardFilters);
  return useMemo(() => {
    if (section) return NO_FILTERS;
    const list = Object.entries(chosen)
      .filter(([, f]) => f.values.length && f.sourceWidgetId !== widgetId)
      .map(([member, f]) => ({ member, values: f.values }))
      .sort((a, b) => a.member.localeCompare(b.member));
    return list.length ? list : NO_FILTERS;
  }, [section, chosen, widgetId]);
}

export function useStaleThresholdMinutes() {
  const section = useContext(WallSectionContext);
  const current = useStore((state) => state.currentDashboard?.stale_threshold_minutes);
  return (section ? section.staleThresholdMinutes : current) || 5;
}
